// Cards for the hunt data tools' step results (2026-09-29 on Staging; the Artemis guide's "Step
// results"): draw odds, unit info, units at a point, harvest statistics, terrain analysis and
// viewshed. Each builder takes a Result and returns a plain object for the card, or null when
// the Result is of another Kind or has nothing to show. Fields the guide doesn't pin down
// (unit Scores, Harvest and Seasons, the Elevation object) are read defensively.
// Also Hunt Planner's hunts (find_hunts and get_hunt_details, API-3887), and the unit's write-up
// and public/private terrain on unit_info (API-3884), read through the planner's own readers.

import { camelKeys } from './keys'
import { unitAbout, unitTerrain } from '../../component/hunt-planner/huntPlannerComponents/huntPlanner-right-toolbar/unit-info/unitInfo'

const num = v => (typeof v === 'number' && Number.isFinite(v) ? v : null)

// A field whatever its case: artemisapi moved some nested results from camelCase to PascalCase
// (8868183) and Staging has sent both, so these are read either way.
const field = (obj, name) => {
  if (!obj || typeof obj !== 'object') return undefined
  if (name in obj) return obj[name]
  const lower = name.toLowerCase()
  const key = Object.keys(obj).find(k => k.toLowerCase() === lower)
  return key === undefined ? undefined : obj[key]
}
const fmt = (v, digits = 0) => {
  const n = num(v)
  return n == null ? null : n.toLocaleString('en-US', { maximumFractionDigits: digits })
}
const pct = v => {
  const n = num(v)
  if (n == null) return null
  return `${n < 1 && n > 0 ? '<1' : Math.round(n)}%`
}
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`
const METERS_PER_MILE = 1609.344
const FEET_PER_METRE = 3.28084

const titleCase = s => String(s).replace(/[_-]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase())

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const shortDate = ymd => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd || '')
  return m ? `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}` : ''
}
const dateRange = (start, end) => {
  const a = shortDate(start), b = shortDate(end)
  return a && b && a !== b ? `${a} – ${b}` : a || b
}

const unitLabel = (code, name) => {
  if (code && name && String(name) !== String(code)) return `Unit ${code} · ${name}`
  if (code) return `Unit ${code}`
  return name || ''
}
const heading = (...parts) => parts.filter(Boolean).join(' · ')

// "Did you mean" chips for units that share a code. ask: what to send when one is picked.
const otherMatches = result => (Array.isArray(result.OtherMatches) ? result.OtherMatches : []).slice(0, 4).map((m, i) => {
  const label = [m.Name || null, m.Code ? `unit ${m.Code}` : null, m.ValidGeo || null].filter(Boolean).join(', ')
  return { key: `${m.ValidGeo || ''}-${m.Code || i}`, label: m.Name || unitLabel(m.Code), ask: `I meant ${label}.` }
})

// A missing figure the server says needs a subscription: { Available: false, Reason: 'subscription_required' }.
const needsSubscription = block => !!block && field(block, 'Available') === false && field(block, 'Reason') === 'subscription_required'

// "unitScore" or "room_to_breathe" as "Unit score", "Room to breathe".
const sentenceCase = k => {
  const words = String(k).replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ').trim().toLowerCase()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

// { key: number } blocks (unit Scores) as [{ label, value }], skipping bookkeeping keys.
const SKIP_KEYS = ['available', 'reason', 'year', 'years', 'source', 'asof']
const figures = (block, limit = 4) => {
  if (!block || typeof block !== 'object' || Array.isArray(block)) return []
  return Object.entries(block)
    .filter(([k, v]) => !SKIP_KEYS.includes(String(k).toLowerCase()) && num(v) != null)
    .slice(0, limit)
    .map(([k, v]) => ({ label: sentenceCase(k), value: fmt(v, 1) }))
}

// A draw's latest figures (Latest.Values): the quota and the points it took, which read on their own.
// The per-choice figures there don't say whether they're odds or counts, so they're left out.
const latestFigures = values => {
  if (!values || typeof values !== 'object') return []
  const pick = (re, label, digits = 0) => {
    const entry = Object.entries(values).find(([k, v]) => re.test(k) && num(v) != null)
    return entry ? { label, value: fmt(entry[1], digits) } : null
  }
  return [pick(/^quota$/i, 'quota'), pick(/minimum preference points/i, 'min points')].filter(Boolean)
}

// A unit's harvest summary: Total is the all-methods, all-seasons harvest row, the same row shape
// as harvest_statistics (Method, Season, Hunters, Harvest, SuccessRate, AvgDaysHunted, …).
const harvestFigures = harvest => {
  const t = field(harvest, 'Total')
  if (!t || typeof t !== 'object') return []
  // Before 8868183 these were totalHarvest, totalHunters, …
  const value = name => num(field(t, name)) ?? num(field(t, `total${name}`))
  return [
    value('Harvest') != null && { label: 'taken', value: fmt(value('Harvest')) },
    value('Hunters') != null && { label: 'hunters', value: fmt(value('Hunters')) },
    value('SuccessRate') != null && { label: 'success', value: pct(value('SuccessRate')) },
    value('AvgDaysHunted') != null && { label: 'days hunted', value: fmt(value('AvgDaysHunted'), 1) }
  ].filter(Boolean)
}

// get_draw_odds: { title, points, hunts: [{ key, name, odds, oddsNote, estimated, tablesNote, latest }], more, others }
const AT_POINTS_NOTES = {
  points_above_table: 'Your points are above this table',
  points_not_in_table: 'No row for your points'
}
export const drawOddsCard = result => {
  if (!result || result.Kind !== 'draw_odds') return null
  const hunts = Array.isArray(result.Hunts) ? result.Hunts : []
  if (hunts.length === 0) return null
  const rows = hunts.slice(0, 4).map((hunt, i) => {
    const table = (Array.isArray(hunt.Tables) ? hunt.Tables : [])[0] || null
    const at = table && table.AtPoints
    const choice = (Array.isArray(hunt.Choices) ? hunt.Choices : []).find(c => num(c.Odds) != null)
    const odds = at && num(at.Odds) != null ? pct(at.Odds) : choice ? pct(choice.Odds) : null
    const latest = hunt.Latest && latestFigures(hunt.Latest.Values)
    return {
      // Resident and nonresident versions of a hunt share its code.
      key: `${hunt.HuntCode || ''}-${hunt.Group || ''}-${i}`,
      name: heading(hunt.Season, hunt.Weapon, hunt.Group, hunt.Sex) || hunt.HuntCode || 'Hunt',
      code: hunt.HuntCode || null,
      dates: dateRange(hunt.Start, hunt.End),
      odds,
      oddsLabel: at && num(at.Odds) != null ? `at ${plural(num(at.Points), 'point')}` : choice ? String(choice.Choice || '1st choice') : null,
      oddsNote: !odds && table && table.AtPointsNote ? AT_POINTS_NOTES[table.AtPointsNote] || null : null,
      estimated: !!(at && at.Estimated),
      tablesNote: hunt.TablesBehindHistory && hunt.TablesYear
        ? `Odds tables are from ${hunt.TablesYear}${hunt.Latest && hunt.Latest.Year ? `; ${hunt.Latest.Year} draw below` : ''}`
        : null,
      latestYear: hunt.Latest && hunt.Latest.Year ? hunt.Latest.Year : null,
      latest: latest || []
    }
  })
  return {
    title: heading([result.State, result.Species].filter(Boolean).join(' '), unitLabel(result.UnitCode, result.UnitName), 'draw odds'),
    // Residency: whose odds these are (from Context.HomeState, or what Claude passed).
    points: [
      result.Residency ? titleCase(String(result.Residency)) : null,
      num(result.Points) != null ? plural(num(result.Points), 'point') : null
    ].filter(Boolean).join(' · ') || null,
    hunts: rows,
    more: Math.max(0, hunts.length - rows.length),
    truncated: !!result.Truncated,
    others: otherMatches(result)
  }
}

// BaseMap's write-up of the unit (API-3884), through the planner's reader:
//   { overview, description, boundary, terrainNotes, accessNotes, weatherNotes, sources: [{ name, url }] } or null.
const aboutOf = about => {
  const a = about && typeof about === 'object' ? unitAbout({ about: camelKeys(about) }) : null
  if (!a) return null
  return {
    overview: a.overview || null,
    description: a.description || null,
    boundary: a.boundary || null,
    terrainNotes: a.terrainNotes || null,
    accessNotes: a.accessNotes || null,
    weatherNotes: a.weatherNotes || null,
    sources: a.sources || []
  }
}

// Elevation and slope on the unit's public and private land (API-3884), each
//   { elevation, median, slope, slopes: [{ key, label, meaning, percent, value }] } or null; null when the unit has neither.
const terrainOf = terrain => {
  const t = terrain && typeof terrain === 'object' ? unitTerrain({ terrain: camelKeys(terrain) }) : null
  if (!t) return null
  const side = s => (s ? {
    elevation: num(s.elevationMin) != null && num(s.elevationMax) != null ? `${fmt(s.elevationMin)} – ${fmt(s.elevationMax)} ft` : null,
    median: num(s.elevationMedian) != null ? `median ${fmt(s.elevationMedian)} ft` : null,
    slope: num(s.slopeMean) != null ? `mean slope ${fmt(s.slopeMean, 1)}°` : null,
    slopes: (s.slope || []).filter(g => num(g.pct) > 0).map(g => ({ key: g.label, label: g.label, meaning: g.range, percent: Math.min(100, g.pct), value: pct(g.pct) }))
  } : null)
  return { public: side(t.public), private: side(t.private) }
}

// get_unit_info: { title, facts, ownership, scores, harvest, seasons, about, terrain, others, focus }
// LandOwnership keys are like usfs_land; public_land is the sum of the public ones, so it's a
// fact line rather than another bar.
const OWNERSHIP_LABELS = { blm: 'BLM', usfs: 'Forest Service', nps: 'Park Service', fws: 'Fish & Wildlife', dod: 'Military', state: 'State', private: 'Private', tribal: 'Tribal' }
const ownershipKey = k => String(k).toLowerCase().replace(/_?land$/, '')
export const unitInfoCard = result => {
  if (!result || result.Kind !== 'unit_info') return null
  const area = [
    num(result.Acres) != null ? `${fmt(result.Acres)} acres` : null,
    num(result.SquareMiles) != null ? `${fmt(result.SquareMiles)} sq mi` : null
  ].filter(Boolean).join(' · ')
  const counties = Array.isArray(result.Counties) && result.Counties.length ? `${result.Counties.join(', ')} ${result.Counties.length === 1 ? 'County' : 'Counties'}` : null
  const land = result.LandOwnership && typeof result.LandOwnership === 'object' ? Object.entries(result.LandOwnership) : []
  const publicShare = land.find(([k, v]) => ownershipKey(k) === 'public' && num(v) != null)
  const ownership = land.filter(([k, v]) => ownershipKey(k) !== 'public' && num(v) != null && v > 0).sort((a, b) => b[1] - a[1]).slice(0, 5)
    .map(([k, v]) => ({ key: k, label: OWNERSHIP_LABELS[ownershipKey(k)] || sentenceCase(ownershipKey(k)), percent: Math.max(0, Math.min(100, v)), value: pct(v) }))
  const scores = needsSubscription(result.Scores) ? { locked: true, rows: [] } : { locked: false, rows: figures(result.Scores) }
  const harvest = needsSubscription(result.Harvest)
    ? { locked: true, rows: [] }
    : { locked: false, rows: harvestFigures(result.Harvest), year: field(result.Harvest, 'Year') || null }
  const allSeasons = Array.isArray(result.Seasons) ? result.Seasons : []
  const HUNT_TYPES = { over_the_counter: 'OTC', limited_drawing: 'Draw' }
  const seasons = allSeasons.slice(0, 5).map((s, i) => ({
    key: s.HuntCode || String(i),
    name: heading(s.Season || s.Name, s.Method || s.Weapon, s.Sex) || s.HuntCode || 'Season',
    dates: dateRange(s.Start, s.End),
    tag: HUNT_TYPES[s.HuntType] || null
  }))
  return {
    title: heading([result.State, result.Species].filter(Boolean).join(' '), unitLabel(result.Code, result.Name)),
    facts: [counties, area, publicShare ? `${pct(publicShare[1])} public land` : null].filter(Boolean),
    ownership,
    scores,
    harvest,
    locked: scores.locked || harvest.locked,
    seasons,
    moreSeasons: Math.max(0, allSeasons.length - seasons.length),
    about: aboutOf(result.About),
    terrain: terrainOf(result.Terrain),
    others: otherMatches(result),
    focus: focusOf({ bbox: result.Bbox, point: result.Point, outline: result.Bbox })
  }
}

// Hunt Planner's hunts (API-3887) ------------------------------------------------------------

const MAX_HUNTS = 6

// Kind is "draw" or "over the counter".
const huntKindTag = kind => {
  const k = String(kind || '').toLowerCase()
  if (!k) return null
  return k === 'draw' ? { label: 'Draw', draw: true } : { label: 'OTC', draw: false }
}

// MinPoints is text ("3"), or for a bonus-point draw the odds at the user's points: { Name, Point, Odds }.
const minPointsText = mp => {
  if (mp == null || mp === '') return null
  if (typeof mp !== 'object') return `${String(mp)} min pts`
  const odds = num(field(mp, 'Odds')), point = num(field(mp, 'Point')), name = field(mp, 'Name')
  if (odds != null) return `${pct(odds)} odds${point != null ? ` at ${plural(point, 'pt')}` : ''}`
  if (point != null) return `${plural(point, 'pt')}${name ? ` · ${name}` : ''}`
  return name ? String(name) : null
}

// The draw filters a search applied, for the card's second line: "Your draw: 5 points · preference point · 20%+ odds".
const drawFiltersLine = f => {
  if (!f || typeof f !== 'object') return null
  const points = num(field(f, 'Points')), odds = num(field(f, 'MinDrawOdds'))
  const parts = [
    points != null ? `${field(f, 'ExactPoints') === true ? 'exactly ' : ''}${plural(points, 'point')}` : null,
    field(f, 'DrawChoice') ? String(field(f, 'DrawChoice')).toLowerCase() : null,
    odds != null ? `${pct(odds)}+ odds` : null,
    field(f, 'Choice') ? String(field(f, 'Choice')) : null
  ].filter(Boolean)
  return parts.length ? `Your draw: ${parts.join(' · ')}` : null
}

const huntFlags = h => [h.IsNewHunt === true && 'New', h.PrivateLandOnly === true && 'Private land only'].filter(Boolean)

// What "Show in Hunt Planner" sets: set_planner_filters' input, which the planner resolves as it
// does Artemis's own picks (HuntType is the planner's key).
const plannerSelection = r => (r.State && r.Species && r.HuntType ? { state: r.State, species: r.Species, hunt_type: r.HuntType } : null)

// find_hunts: { title, sub, hunts: [{ key, code, unit, name, dates, tag, applyFor, minPoints, success, flags }], more, beyond, planner }
// more: hunts in the card not shown; beyond: hunts that matched but weren't sent (Matched over Hunts).
export const huntListCard = result => {
  if (!result || result.Kind !== 'hunt_list') return null
  const hunts = Array.isArray(result.Hunts) ? result.Hunts : []
  if (hunts.length === 0) return null
  const rows = hunts.slice(0, MAX_HUNTS).map((h, i) => ({
    key: `${h.HuntCode || ''}-${h.WebHuntId ?? i}`,
    code: h.HuntCode || null,
    unit: h.Unit || null,
    name: heading(h.Weapon ? titleCase(h.Weapon) : null, h.Sex ? sentenceCase(h.Sex) : null) || h.HuntCode || 'Hunt',
    dates: dateRange(h.Start, h.End),
    tag: huntKindTag(h.Kind),
    applyFor: h.ApplyFor || null,
    minPoints: minPointsText(h.MinPoints),
    success: num(h.Success) != null ? `${pct(h.Success)} success` : null,
    flags: huntFlags(h)
  }))
  const matched = num(result.Matched) ?? hunts.length
  return {
    title: heading([result.State, result.Species].filter(Boolean).join(' '), matched ? plural(matched, 'hunt') : 'hunts'),
    sub: drawFiltersLine(result.DrawFilters),
    hunts: rows,
    more: Math.max(0, hunts.length - rows.length),
    beyond: Math.max(0, matched - hunts.length),
    planner: plannerSelection(result)
  }
}

// get_hunt_details: { title, tag, facts, flags, notes, seasons: [{ key, name, dates, notes }], odds: [{ key, label, percent, value }], locked }
// OddsByPoints is empty without the user's Hunt Research (HuntResearch false): an upgrade prompt, not zeros.
export const huntDetailsCard = result => {
  if (!result || result.Kind !== 'hunt_details') return null
  if (!result.HuntCode && !result.Unit) return null
  const seasons = Array.isArray(result.Seasons) ? result.Seasons : []
  const odds = (Array.isArray(result.OddsByPoints) ? result.OddsByPoints : []).filter(o => o && num(o.Odds) != null)
  return {
    title: heading(result.HuntCode, result.Unit),
    tag: huntKindTag(result.HuntKind),
    facts: [
      heading(result.Weapon ? titleCase(result.Weapon) : null, result.Sex ? sentenceCase(result.Sex) : null) || null,
      dateRange(result.Start, result.End) || null,
      result.ApplyFor ? `Apply for ${result.ApplyFor}` : null
    ].filter(Boolean),
    flags: huntFlags(result),
    notes: result.Notes || null,
    seasons: seasons.slice(0, MAX_HUNTS).map((s, i) => ({
      key: `${s.Weapon || ''}-${s.Start || i}`,
      name: s.Weapon ? titleCase(s.Weapon) : 'Season',
      dates: dateRange(s.Start, s.End),
      notes: s.Notes || null
    })),
    odds: odds.slice(0, 8).map(o => ({
      key: String(o.Points),
      label: /^\d+$/.test(String(o.Points).trim()) ? plural(Number(o.Points), 'pt') : String(o.Points),
      percent: Math.max(0, Math.min(100, o.Odds)),
      value: pct(o.Odds)
    })),
    locked: result.HuntResearch === false
  }
}

// get_unit_info for a point: { title, units: [{ key, species, name }] }
export const unitsAtPointCard = result => {
  if (!result || result.Kind !== 'units_at_point') return null
  const units = Array.isArray(result.Units) ? result.Units : []
  if (units.length === 0) return null
  return {
    title: heading(result.County ? `${result.County} County` : null, result.State, 'hunt units here'),
    units: units.slice(0, 8).map((u, i) => ({ key: `${u.LayerId || ''}-${u.Code || i}`, species: u.Species ? titleCase(u.Species) : 'All species', name: unitLabel(u.Code, u.Name) })),
    more: Math.max(0, units.length - 8)
  }
}

// get_harvest_statistics: { title, years: [{ year, total, estimated, success, hunters }], note, rows, rowsYear, others }
// Rows overlap (by method, season, …), so they're never added up; a missing Total says so.
export const harvestCard = result => {
  if (!result || result.Kind !== 'harvest_statistics') return null
  const years = Array.isArray(result.Years) ? result.Years : []
  if (years.length === 0) return null
  const recent = years.slice(-5).reverse()
  const latest = recent[0]
  const latestRows = Array.isArray(latest.Rows) ? latest.Rows : []
  const bestRow = rows => rows.reduce((best, r) => (num(r.Harvest) != null && (!best || r.Harvest > best.Harvest) ? r : best), null)
  return {
    title: heading([result.State, result.Species].filter(Boolean).join(' '), unitLabel(result.UnitCode, result.UnitName), 'harvest'),
    years: recent.map(y => {
      const top = bestRow(Array.isArray(y.Rows) ? y.Rows : [])
      // Total is the whole-unit harvest row (Method and Season usually "all"; API-3649), or null
      // with TotalReason no_published_total. A plain number is read too.
      const totalRow = y.Total && typeof y.Total === 'object' ? y.Total : null
      const total = num(y.Total) ?? num(field(totalRow, 'Harvest'))
      const success = num(field(totalRow, 'SuccessRate')) ?? (top ? num(top.SuccessRate) : null)
      return {
        year: y.Year,
        total: total != null ? fmt(total) : null,
        estimated: y.RecordType === 'estimated',
        success: success != null ? pct(success) : null
      }
    }),
    noTotal: recent.some(y => y.Total == null && y.TotalReason === 'no_published_total'),
    rowsYear: latest.Year,
    rows: latestRows.slice(0, 5).map((r, i) => ({
      key: String(i),
      name: heading(r.Method, r.Season, r.SpecialType) || 'All',
      harvest: fmt(r.Harvest),
      hunters: fmt(r.Hunters),
      success: pct(r.SuccessRate)
    })),
    others: otherMatches(result)
  }
}

// Where "Show on map" goes and what it draws: { bbox, point, fill (GeoJSON), outline (GeoJSON or bbox), pins }.
const focusOf = ({ bbox, point, fill = null, outline = null, pins = [] }) => {
  const b = Array.isArray(bbox) && bbox.length === 4 && bbox.every(Number.isFinite) ? bbox : null
  const p = Array.isArray(point) && point.length === 2 && point.every(Number.isFinite) ? point : null
  if (!b && !p && !fill && !outline && pins.length === 0) return null
  return { bbox: b, point: p, fill, outline: Array.isArray(outline) ? (b ? b : null) : outline, pins }
}

// Elevation comes as m and ft; the exact field names aren't pinned down, so look for the usual ones.
const elevationRange = e => {
  if (!e || typeof e !== 'object') return null
  const feet = (ft, m) => num(field(e, ft)) ?? (num(field(e, m)) != null ? field(e, m) * FEET_PER_METRE : null)
  const min = feet('MinFt', 'MinM'), max = feet('MaxFt', 'MaxM')
  return min != null && max != null ? `${fmt(min)} – ${fmt(max)} ft` : null
}

// get_terrain_analysis: { title, facts, slopes: [{ key, label, percent }], aspects, match, focus }
export const terrainCard = result => {
  if (!result || result.Kind !== 'terrain_analysis') return null
  const area = result.Area || {}
  // Label is the degree range ("16–26°") and Meaning names it ("Significant - bedding terrain"):
  // the bar shows the name, the tooltip both.
  const slopes = (Array.isArray(result.SlopeBands) ? result.SlopeBands : []).filter(b => num(b.Percent) != null && b.Percent > 0)
    .map(b => {
      const name = b.Meaning ? String(b.Meaning).split(' - ')[0] : null
      return {
        key: b.Key || b.Label,
        label: name || b.Label || titleCase(b.Key || ''),
        meaning: [b.Label, b.Meaning].filter(Boolean).join(': ') || null,
        percent: Math.max(0, Math.min(100, b.Percent)),
        value: pct(b.Percent)
      }
    })
  const aspects = (Array.isArray(result.Aspects) ? result.Aspects : []).filter(a => num(a.Percent) != null)
    .sort((a, b) => b.Percent - a.Percent).slice(0, 4).map(a => `${a.Direction} ${pct(a.Percent)}`)
  const match = result.Match || null
  const cells = match && Array.isArray(match.Cells) ? match.Cells.filter(c => Array.isArray(c.Point) && c.Point.length === 2) : []
  return {
    title: heading('Terrain', num(result.AreaKm2) != null ? `${fmt(result.AreaKm2, 1)} km²` : null),
    facts: [
      elevationRange(result.Elevation),
      num(result.MeanSlopeDeg) != null ? `Mean slope ${fmt(result.MeanSlopeDeg, 1)}°` : null
    ].filter(Boolean),
    slopes,
    aspects,
    match: match ? {
      summary: [pct(match.Percent) && `${pct(match.Percent)} of the area matches`, cells.length ? plural(cells.length, 'spot') : null].filter(Boolean).join(' · '),
      elevation: num(match.ElevMinM) != null && num(match.ElevMaxM) != null
        ? `${fmt(match.ElevMinM * FEET_PER_METRE)} – ${fmt(match.ElevMaxM * FEET_PER_METRE)} ft` : null
    } : null,
    focus: focusOf({
      bbox: area.Bbox || bboxOfGeometry(area.Geometry),
      outline: area.Geometry || (Array.isArray(area.Bbox) ? area.Bbox : null),
      pins: cells.slice(0, 20).map(c => c.Point)
    })
  }
}

// get_viewshed: { title, facts, focus }
export const viewshedCard = result => {
  if (!result || result.Kind !== 'viewshed') return null
  const radius = num(result.RadiusMeters)
  return {
    title: heading('Viewshed', radius != null ? `${fmt(radius / METERS_PER_MILE, 1)} mi radius` : null),
    visible: pct(result.VisiblePercent),
    facts: [
      num(result.VisibleKm2) != null ? `${fmt(result.VisibleKm2, 1)} km² visible` : null,
      num(result.ObserverElevationFt) != null ? `From ${fmt(result.ObserverElevationFt)} ft` : null,
      num(result.ObserverHeightM) != null ? `eye height ${fmt(result.ObserverHeightM * FEET_PER_METRE)} ft` : null
    ].filter(Boolean),
    focus: focusOf({ bbox: bboxOfGeometry(result.Visible), point: result.Point, fill: result.Visible || null, pins: Array.isArray(result.Point) ? [result.Point] : [] })
  }
}

// [west, south, east, north] of any GeoJSON geometry, or null.
export const bboxOfGeometry = geometry => {
  let w = Infinity, s = Infinity, e = -Infinity, n = -Infinity
  const walk = c => {
    if (!Array.isArray(c)) return
    if (c.length >= 2 && typeof c[0] === 'number' && typeof c[1] === 'number') {
      w = Math.min(w, c[0]); e = Math.max(e, c[0]); s = Math.min(s, c[1]); n = Math.max(n, c[1])
    } else c.forEach(walk)
  }
  if (!geometry) return null
  if (geometry.type === 'FeatureCollection') (geometry.features || []).forEach(f => f && f.geometry && walk(f.geometry.coordinates))
  else if (geometry.type === 'Feature') walk(geometry.geometry && geometry.geometry.coordinates)
  else if (geometry.type === 'GeometryCollection') (geometry.geometries || []).forEach(g => walk(g.coordinates))
  else walk(geometry.coordinates)
  return Number.isFinite(w) ? [w, s, e, n] : null
}

// The hunt data tools need a hunt data subscription: a failed step with this code gets an upgrade card.
export const needsHuntData = step => step.status === 'failed' && step.message === 'subscription_required'

// All the cards above, by Kind: { kind, card } or null.
export const huntDataCard = result => {
  const builders = {
    draw_odds: drawOddsCard, unit_info: unitInfoCard, units_at_point: unitsAtPointCard, harvest_statistics: harvestCard,
    terrain_analysis: terrainCard, viewshed: viewshedCard, hunt_list: huntListCard, hunt_details: huntDetailsCard
  }
  const build = result && builders[result.Kind]
  const card = build ? build(result) : null
  return card ? { kind: result.Kind, card } : null
}
